﻿import { initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  OAuthProvider,
  PhoneAuthProvider,
  RecaptchaVerifier,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  getRedirectResult,
  linkWithCredential,
  onAuthStateChanged,
  reload,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  updatePhoneNumber,
  updateProfile,
} from "firebase/auth";
import { addDoc, collection, getFirestore, serverTimestamp } from "firebase/firestore";
import { getDataConnect } from "firebase/data-connect";
import { getStorage } from "firebase/storage";

const firebaseConfig = {
  apiKey: import.meta.env.EXPO_PUBLIC_FIREBASE_API_KEY || "",
  authDomain: import.meta.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN || "",
  projectId: import.meta.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID || "",
  storageBucket: import.meta.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: import.meta.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: import.meta.env.EXPO_PUBLIC_FIREBASE_APP_ID || "",
  measurementId: import.meta.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID || "",
};

const REQUIRED_FIREBASE_CONFIG_KEYS = {
  EXPO_PUBLIC_FIREBASE_API_KEY: "apiKey",
  EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN: "authDomain",
  EXPO_PUBLIC_FIREBASE_PROJECT_ID: "projectId",
  EXPO_PUBLIC_FIREBASE_APP_ID: "appId",
};

function getMissingFirebaseConfigKeys() {
  return Object.entries(REQUIRED_FIREBASE_CONFIG_KEYS)
    .filter(([, configKey]) => !firebaseConfig[configKey])
    .map(([envKey]) => envKey);
}

const missingFirebaseConfigKeys = getMissingFirebaseConfigKeys();
const hasFirebaseConfig = missingFirebaseConfigKeys.length === 0;

let appInstance = null;
let authInstance = null;
let firestoreInstance = null;
let dataConnectInstance = null;
let storageInstance = null;
let googleProvider = null;
let appleProvider = null;
let phoneRecaptchaVerifier = null;
let authRuntimeReadyPromise = null;

const AUTH_DIAGNOSTIC_COLLECTION = "webAuthDiagnosticLogs";
const AUTH_DIAGNOSTIC_QUEUE_KEY = "denkup.authDiagnosticQueue.v1";
const authDiagnosticListeners = new Set();
const recentAuthDiagnostics = [];

export const AUTH_STATE_STATUS = {
  CHECKING: "checking",
  AUTHENTICATED: "authenticated",
  SIGNED_OUT: "signed-out",
  UNAVAILABLE: "unavailable",
};

const dataConnectConfig = {
  connector: import.meta.env.EXPO_PUBLIC_SQL_CONNECT_CONNECTOR_ID || "denkup",
  service: import.meta.env.EXPO_PUBLIC_SQL_CONNECT_SERVICE_ID || "denkup-service",
  location: import.meta.env.EXPO_PUBLIC_SQL_CONNECT_LOCATION || "europe-west3",
};

function getBrowserDiagnosticContext() {
  if (typeof window === "undefined") return {};
  return {
    path: window.location?.pathname || "",
    href: window.location?.href || "",
    userAgent: window.navigator?.userAgent || "",
  };
}

function sanitizeAuthError(error) {
  return {
    code: String(error?.code || "auth/unknown"),
    message: String(error?.message || "Auth diagnostic error").slice(0, 500),
    name: String(error?.name || "Error"),
  };
}

function readQueuedAuthDiagnostics() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage?.getItem(AUTH_DIAGNOSTIC_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueuedAuthDiagnostics(items) {
  if (typeof window === "undefined") return;
  try {
    const safeItems = Array.isArray(items) ? items.slice(-50) : [];
    if (safeItems.length) {
      window.localStorage?.setItem(AUTH_DIAGNOSTIC_QUEUE_KEY, JSON.stringify(safeItems));
    } else {
      window.localStorage?.removeItem(AUTH_DIAGNOSTIC_QUEUE_KEY);
    }
  } catch {
    // Local diagnostic queue is best-effort only.
  }
}

function queueAuthDiagnostic(payload) {
  const queued = readQueuedAuthDiagnostics();
  queued.push({
    ...payload,
    queuedAt: new Date().toISOString(),
  });
  writeQueuedAuthDiagnostics(queued);
}

function notifyAuthDiagnostic(payload) {
  recentAuthDiagnostics.push(payload);
  if (recentAuthDiagnostics.length > 20) recentAuthDiagnostics.shift();
  authDiagnosticListeners.forEach((listener) => {
    try {
      listener(payload);
    } catch {
      // Diagnostic listeners must not break auth flows.
    }
  });
}

function buildAuthDiagnosticDocument(payload, { user = null, queued = false } = {}) {
  return {
    ...payload,
    uid: payload.uid || user?.uid || null,
    emailVerified: payload.emailVerified ?? (typeof user?.emailVerified === "boolean" ? user.emailVerified : null),
    queued,
  };
}

async function persistAuthDiagnostic(payload, { user = null, queueOnFailure = true, queued = false } = {}) {
  try {
    const db = getInitializedFirestore();
    await addDoc(collection(db, AUTH_DIAGNOSTIC_COLLECTION), {
      ...buildAuthDiagnosticDocument(payload, { user, queued }),
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (logError) {
    console.warn("Auth diagnostic log yazılamadı.", logError);
    if (queueOnFailure) queueAuthDiagnostic(payload);
    return false;
  }
}

function reportAuthDiagnostic({ operation, error, severity = "error", user = null, details = {} }) {
  const payload = {
    operation,
    severity,
    error: error ? sanitizeAuthError(error) : null,
    uid: user?.uid || null,
    emailVerified: typeof user?.emailVerified === "boolean" ? user.emailVerified : null,
    projectId: firebaseConfig.projectId || null,
    authDomain: firebaseConfig.authDomain || null,
    details,
    client: getBrowserDiagnosticContext(),
  };
  if (error || severity === "error" || severity === "critical") {
    console.warn(`[DenkUP auth] ${operation}`, payload.error);
  } else {
    console.info(`[DenkUP auth] ${operation}`, details);
  }
  notifyAuthDiagnostic(payload);
  persistAuthDiagnostic(payload, { user });
  return payload;
}

async function flushQueuedAuthDiagnostics(user) {
  if (!user?.uid) return;
  const queued = readQueuedAuthDiagnostics();
  if (!queued.length) return;

  const remaining = [];
  for (const diagnostic of queued) {
    const written = await persistAuthDiagnostic(diagnostic, { user, queueOnFailure: false, queued: true });
    if (!written) remaining.push(diagnostic);
  }
  writeQueuedAuthDiagnostics(remaining);
}

export function listenAuthDiagnostics(listener) {
  if (typeof listener !== "function") return () => {};
  authDiagnosticListeners.add(listener);
  recentAuthDiagnostics.forEach((diagnostic) => {
    try {
      listener(diagnostic);
    } catch {
      // Diagnostic listeners must not break auth flows.
    }
  });
  return () => authDiagnosticListeners.delete(listener);
}

function createFirebaseConfigError(serviceName = "Firebase") {
  const missingKeys = getMissingFirebaseConfigKeys();
  const error = new Error(
    `${serviceName} yapılandırması eksik. Eksik çevre değişkenleri: ${missingKeys.join(", ")}`,
  );
  error.code = "firebase/config-missing";
  error.missingKeys = missingKeys;
  return error;
}

export function assertFirebaseWebConfigured(serviceName = "Firebase") {
  if (!hasFirebaseConfig) throw createFirebaseConfigError(serviceName);
}

function getInitializedApp() {
  assertFirebaseWebConfigured("Firebase App");
  if (appInstance) return appInstance;
  appInstance = initializeApp(firebaseConfig);
  return appInstance;
}

async function initializeAuthRuntime(auth) {
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch (error) {
    reportAuthDiagnostic({ operation: "auth.persistence.set", error, severity: "critical" });
    throw error;
  }

  try {
    const redirectResult = await getRedirectResult(auth);
    if (redirectResult?.user) {
      reportAuthDiagnostic({
        operation: "auth.redirect.result",
        severity: "info",
        user: redirectResult.user,
        details: { providerId: redirectResult.providerId || null },
      });
    }
    return redirectResult || null;
  } catch (error) {
    reportAuthDiagnostic({ operation: "auth.redirect.result", error, severity: "error" });
    throw error;
  }
}

function ensureAuthRuntimeReady(auth) {
  if (!authRuntimeReadyPromise) {
    authRuntimeReadyPromise = initializeAuthRuntime(auth);
  }
  return authRuntimeReadyPromise;
}

function getInitializedAuth() {
  assertFirebaseWebConfigured("Firebase Auth");
  if (authInstance) return authInstance;

  const app = getInitializedApp();
  authInstance = getAuth(app);
  authInstance.languageCode = "tr";
  googleProvider = new GoogleAuthProvider();
  appleProvider = new OAuthProvider("apple.com");
  ensureAuthRuntimeReady(authInstance).catch((error) => {
    console.warn("Auth runtime başlatılamadı.", sanitizeAuthError(error));
  });
  return authInstance;
}

function getInitializedFirestore() {
  assertFirebaseWebConfigured("Firebase Firestore");
  if (firestoreInstance) return firestoreInstance;
  firestoreInstance = getFirestore(getInitializedApp());
  return firestoreInstance;
}

function getInitializedDataConnect() {
  assertFirebaseWebConfigured("Firebase Data Connect");
  if (import.meta.env.EXPO_PUBLIC_SQL_CONNECT_ENABLED !== "true") {
    const error = new Error("Firebase Data Connect kapalı. EXPO_PUBLIC_SQL_CONNECT_ENABLED=true olmadan sosyal Data Connect sorguları çalıştırılamaz.");
    error.code = "data-connect/disabled";
    throw error;
  }
  if (dataConnectInstance) return dataConnectInstance;
  dataConnectInstance = getDataConnect(getInitializedApp(), dataConnectConfig);
  return dataConnectInstance;
}

function getInitializedStorage() {
  assertFirebaseWebConfigured("Firebase Storage");
  if (storageInstance) return storageInstance;
  storageInstance = getStorage(getInitializedApp());
  return storageInstance;
}

function mapAuthErrorMessage(error) {
  const code = String(error?.code || "");
  const nativeMessage = String(error?.customData?._tokenResponse?.error?.message || error?.message || "");
  if (code === "auth/app-not-authorized") return "Bu web uygulaması Firebase Authentication için yetkili değil. Firebase authorized domains ve authDomain ayarları kontrol edilmeli.";
  if (code === "auth/captcha-check-failed") return "Güvenlik doğrulaması tamamlanamadı. Lütfen tekrar dene.";
  if (code === "auth/code-expired") return "SMS kodunun süresi doldu. Yeni kod iste.";
  if (code === "auth/account-exists-with-different-credential" || code === "auth/credential-already-in-use") {
    return "Bu telefon numarası başka bir hesapta kullanılıyor.";
  }
  if (code === "auth/error-code-39" || nativeMessage.includes("error-code-39") || nativeMessage.includes("Error code:39")) {
    return "DenkUP-WEB-PHONE-39: SMS gönderimi Firebase reCAPTCHA uygulama doğrulamasında başarısız oldu. Web auth domain, authorized domains ve Browser API key kısıtlarında denkup.com / denkup.firebaseapp.com izinlerini kontrol edin.";
  }
  if (code === "auth/internal-error") return "Firebase telefon doğrulaması dahili hata döndürdü. Web reCAPTCHA, auth domain veya API key kısıtı kontrol edilmeli.";
  if (code === "auth/invalid-app-credential") return "Firebase reCAPTCHA uygulama doğrulama bilgisi geçersiz. Sayfayı yenileyip tekrar deneyin.";
  if (code === "auth/invalid-credential" || code === "auth/wrong-password" || code === "auth/user-not-found") {
    return "E-posta veya şifre hatalı.";
  }
  if (code === "auth/invalid-email") return "E-posta formatı geçersiz.";
  if (code === "auth/invalid-phone-number") return "Geçerli bir telefon numarası gerekiyor. Örnek: +90 5xx xxx xx xx";
  if (code === "auth/invalid-verification-code") return "SMS doğrulama kodu hatalı.";
  if (code === "auth/email-already-in-use") return "Bu e-posta zaten kullanılıyor.";
  if (code === "auth/weak-password") return "Şifre en az 6 karakter olmalı.";
  if (code === "auth/popup-blocked" || code === "auth/cancelled-popup-request") {
    return "Giriş açılır pencere engeli nedeniyle tamamlanamadı.";
  }
  if (code === "auth/operation-not-allowed") return "Bu giriş yöntemi Firebase üzerinde henüz etkin değil.";
  if (code === "auth/quota-exceeded") return "SMS gönderim kotası aşıldı. Firebase bu proje veya numara için yeni SMS göndermeyi geçici olarak engelledi.";
  if (code === "auth/requires-recent-login") return "Bu işlem için yeniden giriş yapman gerekiyor. Çıkış yapıp tekrar giriş yap.";
  if (code === "auth/unauthorized-domain") return "Bu alan adı Firebase Authentication için yetkili değil. Firebase authorized domains içine denkup.com eklenmeli.";
  if (code === "auth/unsupported-phone-region") return "Telefon doğrulaması şu anda sadece Türkiye numaraları için açık. Numaranı +90 5xx xxx xx xx formatında gir.";
  if (code === "auth/network-request-failed") return "Ağ hatası. İnternet bağlantını kontrol et.";
  if (code === "auth/too-many-requests") return "Çok fazla doğrulama denemesi yapıldı. Firebase bu numara veya cihaz için yeni SMS isteğini geçici olarak engelledi.";
  return error?.message || "Kimlik doğrulama hatası oluştu.";
}

function createWebAuthError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function toPublicWebAuthError(error, fallbackCode = "auth/unknown") {
  const code = String(error?.code || fallbackCode);
  const publicError = createWebAuthError(code, mapAuthErrorMessage(error));
  publicError.originalMessage = error?.message || "";
  publicError.customData = error?.customData;
  return publicError;
}

function showProviderRedirectNotice(providerLabel) {
  if (typeof window === "undefined") return;
  window.alert(`${providerLabel} giriş penceresi tarayıcı tarafından engellendi veya tamamlanamadı. Güvenli yönlendirmeli giriş ekranına aktarılıyorsun.`);
}

function shouldUseRedirectFallback(error) {
  return [
    "auth/popup-blocked",
    "auth/cancelled-popup-request",
    "auth/operation-not-supported-in-this-environment",
  ].includes(error?.code);
}

function normalizeTurkishPhoneNumber(value) {
  const raw = String(value || "").trim();
  const digits = raw.replace(/\D/g, "");

  if (raw.startsWith("+")) {
    if (digits.startsWith("90") && digits.length === 12) return `+${digits}`;
    throw createWebAuthError("auth/unsupported-phone-region", "Telefon doğrulaması şu anda sadece Türkiye numaraları için açık. Numaranı +90 5xx xxx xx xx formatında gir.");
  }
  if (digits.startsWith("90") && digits.length === 12) return `+${digits}`;
  if (digits.startsWith("0") && digits.length === 11) return `+90${digits.slice(1)}`;
  if (digits.startsWith("5") && digits.length === 10) return `+90${digits}`;

  throw createWebAuthError("auth/invalid-phone-number", "Geçerli bir telefon numarası gerekiyor. Örnek: +90 5xx xxx xx xx");
}

function getEmailOtpEndpoint() {
  return import.meta.env.EXPO_PUBLIC_EMAIL_OTP_ENDPOINT || "/api/email-otp";
}

async function callEmailOtpEndpoint(payload) {
  const auth = getInitializedAuth();
  const user = auth?.currentUser;
  if (!user) throw createWebAuthError("email-otp/no-session", "E-posta doğrulaması için oturum açmanız gerekiyor.");

  const token = await user.getIdToken(true);
  const response = await fetch(getEmailOtpEndpoint(), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok || data.ok === false) {
    const error = createWebAuthError("email-otp/request-failed", data.message || "E-posta doğrulama işlemi tamamlanamadı.");
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  return data;
}

export function getFirebaseWebStatus() {
  return {
    hasFirebaseConfig,
    missingConfigKeys: getMissingFirebaseConfigKeys(),
    authDomain: firebaseConfig.authDomain || null,
    projectId: firebaseConfig.projectId || null,
  };
}

export function getFirebaseWebFirestore() {
  return getInitializedFirestore();
}

export function getFirebaseWebDataConnect() {
  return getInitializedDataConnect();
}

export function getFirebaseWebStorage() {
  return getInitializedStorage();
}

export function listenAuthState(listener) {
  try {
    return onAuthStateChanged(
      getInitializedAuth(),
      (nextUser) => {
        if (nextUser?.uid) flushQueuedAuthDiagnostics(nextUser).catch((error) => {
          console.warn("Auth diagnostic kuyruğu gönderilemedi.", sanitizeAuthError(error));
        });
        listener(nextUser || null, {
          status: nextUser ? AUTH_STATE_STATUS.AUTHENTICATED : AUTH_STATE_STATUS.SIGNED_OUT,
          source: "firebase-auth",
        });
      },
      (error) => {
        listener(undefined, {
          status: AUTH_STATE_STATUS.UNAVAILABLE,
          source: "firebase-auth",
          error,
        });
      },
    );
  } catch (error) {
    listener(undefined, {
      status: AUTH_STATE_STATUS.UNAVAILABLE,
      source: "firebase-auth",
      error,
    });
    return () => {};
  }
}

export async function loginWithEmailPassword(email, password) {
  const auth = getInitializedAuth();
  try {
    await ensureAuthRuntimeReady(auth);
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    reportAuthDiagnostic({ operation: "auth.login.email", error, user: auth.currentUser });
    throw new Error(mapAuthErrorMessage(error));
  }
}

export async function signupWithEmailPassword(email, password) {
  const auth = getInitializedAuth();
  try {
    await ensureAuthRuntimeReady(auth);
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    reportAuthDiagnostic({ operation: "auth.signup.email", error, user: auth.currentUser });
    throw new Error(mapAuthErrorMessage(error));
  }
}

export async function sendVerificationEmailForUser(user) {
  if (!user) throw new Error("E-posta doğrulaması için aktif kullanıcı bulunamadı.");
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://denkup.com";
    await sendEmailVerification(user, {
      url: `${origin}/email-dogrulama?verifiedLink=1`,
      handleCodeInApp: false,
    });
  } catch (error) {
    reportAuthDiagnostic({ operation: "auth.email_verification.send", error, user });
    throw toPublicWebAuthError(error);
  }
}

export async function sendPasswordResetEmailForAddress(email) {
  const auth = getInitializedAuth();
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!normalizedEmail || !normalizedEmail.includes("@")) {
    throw createWebAuthError("auth/invalid-email", "E-posta formatı geçersiz.");
  }

  try {
    await ensureAuthRuntimeReady(auth);
    const origin = typeof window !== "undefined" ? window.location.origin : "https://denkup.com";
    await sendPasswordResetEmail(auth, normalizedEmail, {
      url: `${origin}/giris`,
      handleCodeInApp: false,
    });
    return true;
  } catch (error) {
    reportAuthDiagnostic({ operation: "auth.password_reset.send", error, user: auth.currentUser, details: { email: normalizedEmail } });
    throw toPublicWebAuthError(error);
  }
}

export async function refreshCurrentWebUser() {
  const user = getInitializedAuth()?.currentUser;
  if (!user) return null;
  await reload(user);
  await user.getIdToken(true);
  return getInitializedAuth()?.currentUser || user;
}

export async function getCurrentWebUserIdToken(forceRefresh = false) {
  const user = getInitializedAuth()?.currentUser;
  if (!user) return "";
  return user.getIdToken(forceRefresh);
}

export async function requestEmailOtpForCurrentUser() {
  try {
    return await callEmailOtpEndpoint({ action: "send" });
  } catch (error) {
    reportAuthDiagnostic({ operation: "auth.email_otp.send", error, user: getInitializedAuth()?.currentUser });
    throw error;
  }
}

export async function verifyEmailOtpForCurrentUser(code) {
  const cleanCode = String(code || "").replace(/\D/g, "");
  if (cleanCode.length !== 6) {
    throw createWebAuthError("email-otp/invalid-code", "6 haneli doğrulama kodunu girmeniz gerekiyor.");
  }

  try {
    const result = await callEmailOtpEndpoint({ action: "verify", code: cleanCode });
    await refreshCurrentWebUser();
    return result;
  } catch (error) {
    reportAuthDiagnostic({ operation: "auth.email_otp.verify", error, user: getInitializedAuth()?.currentUser });
    throw error;
  }
}

async function createPhoneRecaptcha(containerId) {
  const auth = getInitializedAuth();
  const container = document.getElementById(containerId);
  if (!container) throw createWebAuthError("auth/missing-recaptcha-container", "Telefon doğrulama alanı hazırlanamadı.");

  if (phoneRecaptchaVerifier) {
    phoneRecaptchaVerifier.clear?.();
    phoneRecaptchaVerifier = null;
  }

  phoneRecaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
    "expired-callback": () => {
      if (phoneRecaptchaVerifier) {
        phoneRecaptchaVerifier.clear?.();
        phoneRecaptchaVerifier = null;
      }
    },
  });
  await phoneRecaptchaVerifier.render();
  return phoneRecaptchaVerifier;
}

export async function sendPhoneVerificationCodeForCurrentUser(phoneNumber, recaptchaContainerId = "phone-recaptcha-container") {
  const auth = getInitializedAuth();
  const user = auth?.currentUser;
  if (!user) throw createWebAuthError("auth/phone-login-required", "Telefon doğrulaması için oturum açmanız gerekiyor.");

  try {
    const normalizedPhoneNumber = normalizeTurkishPhoneNumber(phoneNumber);
    const verifier = await createPhoneRecaptcha(recaptchaContainerId);
    const provider = new PhoneAuthProvider(auth);
    const verificationId = await provider.verifyPhoneNumber(normalizedPhoneNumber, verifier);
    return { verificationId, phoneNumber: normalizedPhoneNumber };
  } catch (error) {
    reportAuthDiagnostic({ operation: "auth.phone.send_code", error, user, details: { recaptchaContainerId } });
    throw toPublicWebAuthError(error);
  }
}

export async function confirmPhoneVerificationCodeForCurrentUser({ verificationId, code, phoneNumber }) {
  const auth = getInitializedAuth();
  const user = auth?.currentUser;
  if (!user) throw createWebAuthError("auth/phone-login-required", "Telefon doğrulaması için oturum açmanız gerekiyor.");

  const cleanCode = String(code || "").replace(/\D/g, "");
  if (!verificationId || cleanCode.length !== 6) {
    throw createWebAuthError("auth/invalid-verification-code", "SMS ile gelen 6 haneli kodu girmen gerekiyor.");
  }

  try {
    const credential = PhoneAuthProvider.credential(verificationId, cleanCode);
    if (user.phoneNumber) {
      await updatePhoneNumber(user, credential);
    } else {
      await linkWithCredential(user, credential);
    }
    return refreshCurrentWebUser();
  } catch (error) {
    reportAuthDiagnostic({ operation: "auth.phone.confirm_code", error, user });
    throw toPublicWebAuthError(error);
  }
}

export async function updateFirebaseUserDisplayName(user, displayName) {
  if (!user || !displayName) return user;
  await updateProfile(user, { displayName });
  return user;
}

async function signInWithProvider(provider, providerLabel) {
  const auth = getInitializedAuth();
  if (!provider) throw new Error("Giriş sağlayıcısı hazırlanamadı.");
  try {
    await ensureAuthRuntimeReady(auth);
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    if (shouldUseRedirectFallback(error)) {
      try {
        showProviderRedirectNotice(providerLabel);
        reportAuthDiagnostic({
          operation: "auth.provider.redirect_fallback",
          error,
          severity: "info",
          user: auth.currentUser,
          details: { provider: providerLabel },
        });
        await signInWithRedirect(auth, provider);
        return null;
      } catch (redirectError) {
        reportAuthDiagnostic({ operation: "auth.redirect.start", error: redirectError, user: auth.currentUser });
        throw new Error(mapAuthErrorMessage(redirectError));
      }
    }
    reportAuthDiagnostic({ operation: "auth.provider.popup", error, user: auth.currentUser });
    throw new Error(mapAuthErrorMessage(error));
  }
}

export async function loginWithGooglePopup() {
  return signInWithProvider(googleProvider, "Google");
}

export async function loginWithApplePopup() {
  return signInWithProvider(appleProvider, "Apple");
}

export async function logoutCurrentUser() {
  const auth = getInitializedAuth();
  await signOut(auth);
}

